import {
  DialogContent,
  DialogTitle,
  Dialog,
  Divider,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Button,
} from "@mui/material";
import React, { useRef, useState } from "react";
import {
  FaBold,
  FaBolt,
  FaEllipsisH,
  FaRobot,
  FaTrash,
  FaPowerOff,
  FaCog,
  FaUsers,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { automationsService, AutomationStatus } from "services/admin/automationsService";
import { Link } from "react-router-dom";
import { queryClient } from "queryClient";
import { AutomationNameForm } from "components/AutomationNameForm/AutomationNameForm";
import { AutomationContacts } from "components/AutomationContacts/AutomationContacts";
interface AutomationCardProps {
  automation: any;
  onDelete: (automationID: string) => void;
}

export const AutomationCard = ({ automation, onDelete }: AutomationCardProps) => {
  const [moreOptionsAnchorEl, setMoreOptionsAnchorEl] = useState<null | HTMLElement>(null);
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [openContactsDialog, setOpenContactsDialog] = useState(false);

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setMoreOptionsAnchorEl(e.currentTarget);
  };

  const handleDeleteAutomation = async () => {
    if (window.confirm("Вы уверены, что хотите удалить автоматизацию?")) {
      onDelete(automation.id);
    }
  };

  const handleChangeStatus = async () => {
    await automationsService.updateAutomation(automation.id, {
      status:
        automation.status === AutomationStatus.ACTIVE
          ? AutomationStatus.INACTIVE
          : AutomationStatus.ACTIVE,
    });
    toast.success("Статус автоматизации успешно обновлен");
    queryClient.setQueryData(["getAutomations"], (old: any) => {
      return old.map((automation: any) => {
        if (automation.id === automation.id) {
          return {
            ...automation,
            status:
              automation.status === AutomationStatus.ACTIVE
                ? AutomationStatus.INACTIVE
                : AutomationStatus.ACTIVE,
          };
        }
        return automation;
      });
    });
  };

  const handleSubmitAutomation = async (data: any) => {
    try {
      await automationsService.updateAutomation(automation.id, {
        name: data.name,
        aiEnabled: data.aiEnabled,
      });
      toast.success("Название автоматизации успешно обновлено");
      queryClient.setQueryData(["getAutomations"], (old: any) => {
        return old.map((automation: any) => {
          if (automation.id === automation.id) {
            return {
              ...automation,
              name: data.name,
              aiEnabled: data.aiEnabled,
            };
          }
          return automation;
        });
      });
      setOpenSettingsDialog(false);
    } catch (error) {
      console.error("Error updating automation name: ", error);
      toast.error("Ошибка при обновлении названия автоматизации");
      setOpenSettingsDialog(false);
    }
  };

  return (
    <div
      className="relative"
      style={{
        position: "relative",
      }}
    >
      <div
        key={automation.id}
        className="automation-card cursor-pointer border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all duration-300 w-full hover:bg-gray-50 hover:border-gray-300 hover:shadow-md hover:dark:bg-gray-900 hover:dark:border-gray-700 hover:dark:shadow-md dark:border-gray-700 dark:bg-gray-800"
      >
        <div className="automation-card-header text-md font-bold mb-4 flex items-center justify-between">
          <Link to={`/admin/automations/create/${automation.templateID}/${automation.id}`}>
            <div className="flex items-center gap-2">
              <FaBolt
                size={20}
                color={
                  automation.status === AutomationStatus.ACTIVE
                    ? "var(--color-success)"
                    : "var(--color-danger)"
                }
              />
              <span>{automation.name}</span>
            </div>
          </Link>
          {/* Dots button */}
          <button
            className="dots-button text-gray-500 p-2 rounded-full hover:bg-gray-100"
            onClick={handleOpenMenu}
          >
            <FaEllipsisH />
          </button>
        </div>
        <div className="flex items-center gap-4 mb-4">
          <div className="contacts-count">
            <div className="text-sm text-gray-500">Контакты</div>
            <div className="text-lg font-bold">{automation.automationRuns || "-"}</div>
          </div>
          <div className="conversions-count">
            <div className="text-sm text-gray-500">Конверсии</div>
            <div className="text-lg font-bold">
              {automation.conversions ? `${automation.conversions}%` : "-"}
            </div>
          </div>
        </div>
        <div className="external-account-user-name text-sm text-gray-500">
          {automation.externalAccountUserName}
        </div>
      </div>
      <Menu
        anchorEl={moreOptionsAnchorEl}
        open={Boolean(moreOptionsAnchorEl)}
        onClose={() => setMoreOptionsAnchorEl(null)}
      >
        <MenuItem>
          <div className="flex items-center justify-between w-100">
            <div className="flex items-center gap-2">
              <FaPowerOff size={12} />
              <span>Включить</span>
            </div>
            <div>
              <Switch
                size="small"
                defaultChecked={automation.status === AutomationStatus.ACTIVE}
                onChange={handleChangeStatus}
              />
            </div>
          </div>
        </MenuItem>
        <Divider />
        <MenuItem>
          <div
            className="flex items-center gap-2"
            onClick={() => {
              setMoreOptionsAnchorEl(null);
              setOpenContactsDialog(true);
            }}
          >
            <FaUsers size={12} />
            Контакты
          </div>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenSettingsDialog(true);
            setMoreOptionsAnchorEl(null);
          }}
        >
          <div className="flex items-center gap-2">
            <FaCog size={12} />
            Настройки
          </div>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteAutomation} color="error">
          <div className="flex items-center gap-2 text-red-500">
            <FaTrash size={12} />
            Удалить автоматизацию
          </div>
        </MenuItem>
      </Menu>
      {/* Settings dialog */}
      <Dialog open={openSettingsDialog} onClose={() => setOpenSettingsDialog(false)}>
        <DialogTitle>Настройки автоматизации</DialogTitle>
        <DialogContent>
          <AutomationNameForm automation={automation} onSubmit={handleSubmitAutomation} />
        </DialogContent>
      </Dialog>
      {/* Contacts dialog */}
      <Dialog fullScreen open={openContactsDialog} onClose={() => setOpenContactsDialog(false)}>
        <DialogTitle>
          {/* Back button */}
          <Button onClick={() => setOpenContactsDialog(false)}>
            <div className="flex items-center gap-2">
              <FaArrowLeft size={12} />
              <span>Автоматизации</span>
            </div>
          </Button>
        </DialogTitle>
        <DialogContent className="md:w-[800px] mx-auto">
          <AutomationContacts automation={automation} />
        </DialogContent>
      </Dialog>
    </div>
  );
};
